"use client";

import { createContext, useContext, useState, ReactNode } from "react";

/** Tabs available in the sliding notifications panel. */
export type PanelTab = "all" | "archived" | "changes" | "waitlist";

interface NotificationsContextValue {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    /** Open the panel jumping straight to a specific tab. */
    openTo: (tab: PanelTab) => void;
    /** Consumed once by the panel to select the requested tab, then cleared. */
    pendingTab: PanelTab | null;
    consumePendingTab: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [pendingTab, setPendingTab] = useState<PanelTab | null>(null);

    return (
        <NotificationsContext.Provider
            value={{
                isOpen,
                open: () => setIsOpen(true),
                close: () => setIsOpen(false),
                toggle: () => setIsOpen((prev) => !prev),
                openTo: (tab: PanelTab) => {
                    setPendingTab(tab);
                    setIsOpen(true);
                },
                pendingTab,
                consumePendingTab: () => setPendingTab(null),
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotificationsPanel() {
    const ctx = useContext(NotificationsContext);
    if (!ctx) {
        throw new Error("useNotificationsPanel must be used within NotificationsProvider");
    }
    return ctx;
}
